// The intake "Start negotiation" opens: a self-contained dark wizard in the
// renewal intake demo's style. The playbook's needs are asked one by one in
// a conversation; each is answered in the chat box (typed, picked from the
// quick replies, or attached as a document), delegated to someone from the
// directory with a due date, or skipped. What InfinityLoop can pull itself is
// stated, not asked. Finishing starts the negotiation; the delegated needs
// become the asks the project waits on. Later steps reopen it for one thing:
// the vendor's answer that round 2 is prepared from, or the signed agreement.

import { FC, ReactNode, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
    ArrowRightOutlined,
    CheckOutlined,
    CloseOutlined,
    HourglassFilled,
} from "@ant-design/icons";
import { clsx } from "clsx";
import { Result } from "../../../../design-system";
import { useUserState } from "../../../../common/UserContext";
import { getInitials } from "../../../../common/components/NameAvatar";
import { AppHeader } from "../../../layout/AppHeader";
import { BrandMark } from "../../../intake/components/MessageBubble";
import { ChatBox, isPulled, lowerFirst, said } from "../components/AskChat";
import { dealOf, projectOf, vendorOf } from "../model/derived";
import { longDate, shortDate } from "../model/format";
import { DEMO_TODAY } from "../model/fixtures";
import {
    completeIntake,
    decide,
    requestUpdate,
    sign,
    submit,
    updateProposal,
} from "../model/store";
import type { Fulfilment, Need, NeedAnswer, UsageProject } from "../model/types";
import { IntakeStage, usageProjectPath } from "../paths";
import classes from "./UsageIntakeView.module.less";

const BOT_DELAY_MS = 700;

const BotMsg: FC<{ children: ReactNode }> = ({ children }) => (
    <div className={classes.msg}>
        <div className={classes.msgAvatar}>
            <BrandMark />
        </div>
        <div className={classes.msgBody}>
            <div className={classes.msgText}>{children}</div>
        </div>
    </div>
);

const UserMsg: FC<{ initials: string; children: ReactNode }> = ({ initials, children }) => (
    <div className={clsx(classes.msg, classes.msgUser)}>
        <div className={clsx(classes.msgAvatar, classes.msgAvatarUser)}>{initials}</div>
        <div className={classes.msgBody}>
            <div className={classes.msgBubble}>{children}</div>
        </div>
    </div>
);

const Typing: FC = () => (
    <BotMsg>
        <span className={classes.typing}>
            <span />
            <span />
            <span />
        </span>
    </BotMsg>
);

/** What the wizard collects and what happens when it is done. */
interface Flow {
    needs: Need[];
    /** What the bot says first; a follow-up goes straight to its one ask. */
    opener?: ReactNode;
    /** The closing line once everything is answered; the start flow builds its own. */
    closing?: string;
    /** The button that ends the wizard, when nothing is outstanding. */
    submitLabel: string;
    /** Only the start flow hands things to other people or skips them. */
    delegable: boolean;
    finish: (resolved: Record<string, Fulfilment>) => void;
}

const needOf = (
    id: string,
    title: string,
    why: string,
    question: string,
    due: string,
    answer: NeedAnswer = { kind: "document" }
): Need => ({
    id,
    title,
    why,
    question,
    answer,
    suggest: { mode: "answer", value: "" },
    waived: "",
    ask: "",
    due,
});

const answered = (how: Fulfilment | undefined): string =>
    how?.mode === "answer" ? String(how.value) : "";

/** The later intakes: one thing each, from the user, then straight on. */
const followUp = (
    project: UsageProject,
    stage: IntakeStage,
    fulfilments: Record<string, Fulfilment>
): Flow => {
    const vendor = vendorOf(project).name;
    const { deliverable } = project;
    if (stage === "revision") {
        const need = needOf(
            "playbook-update",
            "Playbook update",
            `InfinityLoop revises ${deliverable.name}`,
            `What should change in ${deliverable.name}? Say what to keep, drop or redo, in a line or two.`,
            deliverable.revisedOn ?? DEMO_TODAY,
            { kind: "text", suggested: "" }
        );
        return {
            needs: [need],
            closing: `Thanks. The revised ${deliverable.name.replace(/^the /, "")} is ready within 3 business days.`,
            submitLabel: "Send the update request",
            delegable: false,
            finish: (resolved) =>
                requestUpdate(
                    project.id,
                    answered(resolved[need.id]),
                    deliverable.revisedOn ?? DEMO_TODAY
                ),
        };
    }
    if (stage === "dismiss") {
        const need = needOf(
            "dismissal",
            "Why the growth is expected",
            "The opportunity is set aside; the usage stays on record for the renewal",
            "Why is the consumption growth expected? One line is enough.",
            DEMO_TODAY,
            { kind: "text", suggested: "" }
        );
        return {
            needs: [need],
            closing:
                "Recorded. The opportunity is set aside; the next usage report will say if the pace changes.",
            submitLabel: "Dismiss the opportunity",
            delegable: false,
            finish: (resolved) =>
                decide(project.id, {
                    kind: "dismissed",
                    reason: answered(resolved[need.id]),
                    on: DEMO_TODAY,
                }),
        };
    }
    if (stage === "cancel") {
        const need = needOf(
            "cancellation",
            "Cancellation",
            "The negotiation closes without a signature",
            "Why is the deal cancelled? One line is enough.",
            DEMO_TODAY,
            { kind: "text", suggested: "" }
        );
        return {
            needs: [need],
            closing: "Recorded. The negotiation closes.",
            submitLabel: "Cancel the deal",
            delegable: false,
            finish: (resolved) =>
                decide(project.id, {
                    kind: "cancelled",
                    reason: answered(resolved[need.id]),
                    on: DEMO_TODAY,
                }),
        };
    }
    // The vendor came back: first whether it is a proposal or the signed agreement, then the document itself.
    const SIGNED = "The signed agreement";
    const PROPOSAL = "An updated proposal to negotiate further";
    const outcome = needOf(
        "vendor-outcome",
        "Final or signed?",
        "A proposal goes to analysis for round 2; a signature closes the negotiation",
        `What did ${vendor} come back with: an updated proposal, or the final agreement, signed?`,
        deliverable.proposalOn ?? DEMO_TODAY,
        { kind: "choice", options: [PROPOSAL, SIGNED], suggested: PROPOSAL }
    );
    const signed = (resolved: Record<string, Fulfilment | undefined>) =>
        answered(resolved[outcome.id]) === SIGNED;
    const proposal = needOf(
        "vendor-proposal",
        "Updated proposal",
        `Round 2 is prepared from ${vendor}'s answer`,
        "Attach their proposal, or summarize it in a line.",
        deliverable.proposalOn ?? DEMO_TODAY
    );
    const agreement = needOf(
        "signed-agreement",
        "Signed agreement",
        "The savings report is prepared from the signed terms",
        "Congratulations. Attach the signed agreement; the savings report is prepared from the signed terms and the negotiation closes.",
        DEMO_TODAY
    );
    const isSigned = signed(fulfilments);
    return {
        // The second ask depends on the first answer, so the list grows as the user answers.
        needs: [outcome, ...(fulfilments[outcome.id] ? [isSigned ? agreement : proposal] : [])],
        closing: isSigned
            ? "Thanks. The negotiation closes on the signed terms; the savings report follows."
            : `Thanks. Round 2 of ${deliverable.name} is prepared from ${vendor}'s answer.`,
        submitLabel: isSigned ? "Close the negotiation" : "Submit for analysis",
        delegable: false,
        finish: (resolved) =>
            signed(resolved)
                ? sign(project.id, answered(resolved[agreement.id]), DEMO_TODAY)
                : updateProposal(
                      project.id,
                      answered(resolved[proposal.id]),
                      deliverable.proposalOn ?? DEMO_TODAY
                  ),
    };
};

type AskState = "pending" | "waiting" | "done";

const askState = (how: Fulfilment | undefined): AskState =>
    !how ? "pending" : how.mode === "delegate" || how.mode === "pull" ? "waiting" : "done";

/** The asks in order; the icon says whether each is still to answer, waiting on someone, or done. */
const AsksRail: FC<{
    needs: Need[];
    resolved: (need: Need) => Fulfilment | undefined;
    current?: Need;
}> = ({ needs, resolved, current }) => (
    <aside className={classes.rail}>
        <div className={classes.railCard}>
            <div className={classes.sectionLabel}>Asks</div>
            {needs.map((need) => {
                const how = resolved(need);
                const state = askState(how);
                const hint = !how
                    ? need.why
                    : how.mode === "delegate"
                      ? `Waiting on ${how.to} · due ${shortDate(how.due)}`
                      : how.mode === "pull"
                        ? `Waiting on InfinityLoop · due ${shortDate(need.due)}`
                        : said(need, how);
                return (
                    <div
                        key={need.id}
                        className={clsx(
                            classes.ctxRow,
                            state === "done" && classes.ctxRowDone,
                            state === "waiting" && classes.ctxRowWaiting,
                            current?.id === need.id && classes.ctxRowCurrent
                        )}
                    >
                        <div className={classes.ctxCheck}>
                            {state === "done" ? (
                                <CheckOutlined />
                            ) : state === "waiting" ? (
                                <HourglassFilled />
                            ) : null}
                        </div>
                        <div className={classes.ctxBody}>
                            <div className={classes.ctxLabel}>{need.title}</div>
                            <div className={classes.ctxHint}>{hint}</div>
                        </div>
                    </div>
                );
            })}
        </div>
    </aside>
);

export const UsageIntakeView: FC = () => {
    const { projectId = "" } = useParams();
    const [params] = useSearchParams();
    const stage = params.get("for") as IntakeStage | null;
    const navigate = useNavigate();
    const { me } = useUserState();
    const project = projectOf(projectId);
    const [fulfilments, setFulfilments] = useState<Record<string, Fulfilment>>({});
    // The bot "types" for a moment before each of its turns.
    const [typing, setTyping] = useState(true);
    const transcriptRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!typing) return;
        const timer = window.setTimeout(() => setTyping(false), BOT_DELAY_MS);
        return () => window.clearTimeout(timer);
    }, [typing]);
    useEffect(() => {
        transcriptRef.current?.scrollTo({ top: transcriptRef.current.scrollHeight });
    }, [typing, fulfilments]);
    if (!project) return <Result status="404" title="No such project in the demo" />;

    const deal = dealOf(project);
    const initials = getInitials(me.name || me.email || "user").toUpperCase();
    const close = () => navigate(usageProjectPath(project.id));

    // Pulled needs are fulfilled from the start; the others are asked in order.
    const resolved = (need: Need): Fulfilment | undefined =>
        isPulled(need) ? need.suggest : fulfilments[need.id];
    const pulled = project.needs.filter(isPulled);
    const deliverable = project.deliverable.name;

    // The start of the negotiation: with asks outstanding, they go out and the project waits; with none, it goes straight to analysis.
    const startFlow: Flow = {
        needs: project.needs.filter((n) => !isPulled(n)),
        opener: (
            <>
                The opportunity report is built from the usage data we have. The playbook needs a
                few more things; I will ask them one at a time. Answer, hand it to the person who
                knows, or skip it and we proceed on an assumption.
                {pulled.length > 0 &&
                    ` ${pulled
                        .map(
                            (n) =>
                                `${n.title.replace(/^The /, "the ").replace(/^./, (c) => c.toUpperCase())} we pull ourselves, ${lowerFirst(n.pullable!)}`
                        )
                        .join("; ")}.`}
            </>
        ),
        submitLabel: "Submit for analysis",
        delegable: true,
        finish: (all) => {
            completeIntake(project.id, { fulfilments: all, on: DEMO_TODAY });
            if (!Object.values(all).some((h) => h.mode === "delegate"))
                submit(project.id, DEMO_TODAY);
        },
    };
    const flow = stage ? followUp(project, stage, fulfilments) : startFlow;
    const asks = flow.needs;
    const current = asks.find((n) => !fulfilments[n.id]);
    const allAnswered = !current;

    const commit = (need: Need, how: Fulfilment) => {
        setFulfilments((f) => ({ ...f, [need.id]: how }));
        setTyping(true);
    };
    const count = (mode: Fulfilment["mode"]) =>
        asks.filter((n) => fulfilments[n.id]?.mode === mode).length;
    const outstanding = count("delegate");

    const finish = () => {
        flow.finish(Object.fromEntries([...pulled, ...asks].map((n) => [n.id, resolved(n)!])));
        navigate(usageProjectPath(project.id));
    };

    // The conversation so far: the opener, then each ask up to the current one.
    const turns: ReactNode[] = flow.opener ? [<BotMsg key="opener">{flow.opener}</BotMsg>] : [];
    for (const need of asks) {
        turns.push(<BotMsg key={`q-${need.id}`}>{need.question ?? need.title}</BotMsg>);
        const how = fulfilments[need.id];
        if (!how) break;
        turns.push(
            <UserMsg key={`a-${need.id}`} initials={initials}>
                {said(need, how)}
            </UserMsg>
        );
    }
    if (allAnswered) {
        turns.push(
            <BotMsg key="closing">
                {flow.closing ?? (
                    <>
                        That is everything:{" "}
                        {[
                            count("answer") > 0 && `${count("answer")} answered`,
                            outstanding > 0 && `${outstanding} delegated`,
                            count("waive") > 0 && `${count("waive")} skipped`,
                        ]
                            .filter(Boolean)
                            .join(", ")}
                        .{" "}
                        {outstanding === 0
                            ? `Nothing is outstanding; the project can go to analysis now, and ${deliverable} is ready before the ${longDate(deal.term.noticeBy)} notice.`
                            : `Once the ${outstanding === 1 ? "ask is" : "asks are"} answered, the project goes to analysis and ${deliverable} is ready before the ${longDate(deal.term.noticeBy)} notice.`}
                    </>
                )}
            </BotMsg>
        );
    }
    // While the bot types, its latest turn is hidden behind the dots.
    const shown = typing ? turns.slice(0, -1) : turns;

    return (
        <div className={classes.page}>
            <AppHeader me={me} />
            <div className={classes.topBar}>
                <div className={classes.topBarInner}>
                    <div className={classes.topLeft}>
                        <span className={classes.brand}>Guided Intake</span>
                        <span className={classes.divider} />
                        <span className={classes.projectName}>{project.name}</span>
                    </div>
                    <button
                        type="button"
                        className={classes.closeBtn}
                        aria-label="Close"
                        onClick={close}
                    >
                        <CloseOutlined />
                    </button>
                </div>
            </div>

            <div className={classes.split}>
                <div className={classes.flow}>
                    <div className={classes.flowScroll} ref={transcriptRef}>
                        {shown}
                        {typing && <Typing />}
                    </div>
                    {!typing && current && (
                        <ChatBox
                            key={current.id}
                            className={classes.chatArea}
                            need={current}
                            allowSkip={flow.delegable}
                            allowDelegate={flow.delegable}
                            onCommit={(how) => commit(current, how)}
                        />
                    )}
                    {!typing && allAnswered && (
                        <div className={classes.composer}>
                            <button type="button" className={classes.primaryBtn} onClick={finish}>
                                {outstanding === 0
                                    ? flow.submitLabel
                                    : `Send the ${outstanding === 1 ? "ask" : "asks"}`}{" "}
                                <ArrowRightOutlined />
                            </button>
                        </div>
                    )}
                </div>
                <AsksRail needs={asks} resolved={resolved} current={current} />
            </div>
        </div>
    );
};
